import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api/axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token")); // Add token state
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(
    () => localStorage.getItem("operis_onboarding_complete") === "true"
  );

  // 🔐 LOGIN
  const loginWithCredentials = async (email, password, twoFactorCode = null, trustDevice = false) => {
    try {
      setAuthError(null);

      const payload = { email, password };
      if (twoFactorCode) payload.twoFactorCode = twoFactorCode;
      if (trustDevice) payload.trustDevice = true;

      // Attach stored device trust token if available
      const storedTrust = localStorage.getItem(`operis_device_trust_${email.toLowerCase()}`);
      if (storedTrust && !twoFactorCode) payload.deviceTrustToken = storedTrust;

      const res = await api.post("/api/auth/login", payload);

      // If 2FA is required, return the response without storing token
      if (res.data.requiresTwoFactor) {
        return { requiresTwoFactor: true };
      }

      // Store device trust token if the server returned one
      if (res.data.deviceTrustToken) {
        localStorage.setItem(`operis_device_trust_${email.toLowerCase()}`, res.data.deviceTrustToken);
      }

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      
      setToken(res.data.token); // Update token state
      setUser(res.data.user);
      
      console.log('🔑 Login successful, token stored:', res.data.token.substring(0, 20) + '...');
      return res.data.user;
    } catch (err) {
      setAuthError(err.response?.data?.message || "Login failed");
      throw err;
    }
  };

  const register = async (data) => {
    const res = await api.post("/api/auth/register", data);

    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    
    setToken(res.data.token); // Update token state
    setUser(res.data.user);
    
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null); // Clear token state
    setUser(null);
    console.log('🔐 Logout successful, token and user cleared');
  };

  const getAccessToken = () => {
    return localStorage.getItem("token");
  };

  const completeOnboarding = () => {
    localStorage.setItem("operis_onboarding_complete", "true");
    setHasSeenOnboarding(true);
  };

  const resetOnboarding = () => {
    localStorage.removeItem("operis_onboarding_complete");
    setHasSeenOnboarding(false);
  };

  // 🔁 Load user from storage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      console.log('🔐 User loaded from storage, token exists');
    }

    setLoading(false);
  }, []);

  // Log auth state changes
  useEffect(() => {
    console.log('🔐 Auth State Updated:', { 
      hasUser: !!user, 
      hasToken: !!token,
      userId: user?.id,
      tokenLength: token?.length
    });
  }, [user, token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token, // Expose token to consumers
        loading,
        isAuthenticated: !!user,
        authError,
        hasSeenOnboarding,
        loginWithCredentials,
        register,
        logout,
        getAccessToken,
        completeOnboarding,
        resetOnboarding,
        refreshCurrentUser: async () => {
          try {
            const res = await api.get("/api/auth/me");
            const u = res.data;
            if (u && u.id) {
              setUser(u);
              localStorage.setItem("user", JSON.stringify(u));
              return u;
            }
          } catch (e) {
            console.error("refreshCurrentUser failed:", e);
          }
          return null;
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ✅ Safe hook
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};